/**
 * Pure custody-rotation helpers — derive which parent has the child on a given
 * day from a CustodySchedule. Kept framework-free and deterministic (caller
 * passes the reference day). Two-parent model: 'A' (schedule owner) / 'B'.
 *
 *  - week-week : 7-day alternation starting on `startDate` (A first).
 *  - 2-2-3     : classic 14-day cycle (A A B B A A A | B B A A B B B).
 *  - custom    : not auto-derivable → all 'A' (UI shows a note to use events).
 */
import type { CustodyPattern, CustodySchedule } from '@solandia/core';
import { daysBetween, parseIsoDate, startOfUtcDay } from '@solandia/core';

export type CustodyParent = 'A' | 'B';

/** 14-day 2-2-3 template (true = parent A). */
const TWO_TWO_THREE: boolean[] = [
  true, true, // 2 with A
  false, false, // 2 with B
  true, true, true, // 3 with A
  false, false, // 2 with B
  true, true, // 2 with A
  false, false, false, // 3 with B
];

export function parentOnDay(
  schedule: CustodySchedule,
  day: Date,
): CustodyParent {
  const start = startOfUtcDay(parseIsoDate(schedule.startDate));
  const offset = daysBetween(start, startOfUtcDay(day));
  return resolve(schedule.pattern, offset);
}

function resolve(pattern: CustodyPattern, offsetDays: number): CustodyParent {
  switch (pattern) {
    case 'week-week': {
      const week = Math.floor(mod(offsetDays, 14) / 7);
      return week === 0 ? 'A' : 'B';
    }
    case '2-2-3': {
      const idx = mod(offsetDays, 14);
      return TWO_TWO_THREE[idx] ? 'A' : 'B';
    }
    case 'custom':
    default:
      return 'A';
  }
}

/** Always-positive modulo. */
function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

export interface CustodyDay {
  date: Date;
  parent: CustodyParent;
  isToday: boolean;
}

/**
 * Build a window of N days (default 14) starting at the Monday on/before `from`,
 * each tagged with the owning parent. For the visual schedule grid.
 */
export function buildCustodyWindow(
  schedule: CustodySchedule,
  from: Date,
  days = 14,
): CustodyDay[] {
  const todayKey = startOfUtcDay(from).getTime();
  const start = mondayOnOrBefore(from);
  const out: CustodyDay[] = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(start.getTime() + i * 86_400_000);
    out.push({
      date: d,
      parent: parentOnDay(schedule, d),
      isToday: startOfUtcDay(d).getTime() === todayKey,
    });
  }
  return out;
}

function mondayOnOrBefore(d: Date): Date {
  const s = startOfUtcDay(d);
  // getUTCDay: 0=Sun..6=Sat → days back to Monday.
  const back = (s.getUTCDay() + 6) % 7;
  return new Date(s.getTime() - back * 86_400_000);
}
